import {
  User,
  ChevronRight,
  LogOut,
  CreditCard,
  HelpCircle,
  // Shield removed — Data & Privacy page not used on native
  Settings,
  Sun,
  Moon,
  Smartphone,
  Receipt,
  Trash2,
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { GGJLogoMark } from '../Brand/GGJLogo';
import LoadingSpinner from '../LoadingSpinner';
import { APP_VERSION } from '../../config/appVersion';
import { useTheme } from '../../contexts/ThemeContext';
import { usePlanStatus } from '../../hooks/usePlanStatus';
import { supabase } from '../../lib/supabase';
import { PlanIcon } from '../../utils/planDisplay';
import { formatPlanTimelineLocalized, getLocalizedPlanLabel } from '../../utils/planI18n';
import type { DashboardPage } from '../../modules/dashboard/components/DashboardContainer';
import AccountDeletionFlow from '../../modules/dashboard/components/UserProfile/AccountDeletionFlow';

const THEME_KEY = 'app-theme';
type ThemePreference = 'light' | 'auto' | 'dark';

interface MobileProfileMenuProps {
  onSignOut: () => void | Promise<void>;
  onNavigate: (page: DashboardPage) => void;
}

interface MenuItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  page?: DashboardPage;
  danger?: boolean;
}

const MENU_GROUPS: { items: MenuItem[] }[] = [
  {
    items: [
      { id: 'profile-info',    icon: <User size={16} />,       label: 'Profile Information',    page: 'profile-info'     },
      { id: 'billing',         icon: <CreditCard size={16} />, label: 'Subscription & Billing', page: 'credits'          },
      { id: 'payment-history', icon: <Receipt size={16} />,    label: 'Payment History',        page: 'payment-history'  },
    ],
  },
  {
    items: [
      { id: 'support', icon: <HelpCircle size={16} />, label: 'Help & Support', page: 'support' },
    ],
  },
];

/**
 * Native-only "Me" profile screen.
 * Renders a user avatar card + grouped menu rows + sign-out button.
 */
const MobileProfileMenu: React.FC<MobileProfileMenuProps> = ({ onSignOut, onNavigate }) => {
  const { t, i18n } = useTranslation();
  const planStatus = usePlanStatus();
  const { setTheme } = useTheme();
  const [name, setName] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [signingOut, setSigningOut] = useState(false);
  const [showDeletionFlow, setShowDeletionFlow] = useState(false);

  // Read stored preference: 'light' | 'dark' | anything-else → 'auto'
  const [themePref, setThemePref] = useState<ThemePreference>(() => {
    const s = localStorage.getItem(THEME_KEY);
    return s === 'light' ? 'light' : s === 'dark' ? 'dark' : 'auto';
  });

  const handleThemePref = (pref: ThemePreference) => {
    setThemePref(pref);
    if (pref === 'auto') {
      localStorage.removeItem(THEME_KEY);
      // Let ThemeContext's interval pick up the time-based theme on next tick
      const hour = new Date().getHours();
      setTheme(hour >= 6 && hour < 18 ? 'light' : 'dark');
    } else {
      setTheme(pref);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) { setLoadingUser(false); return; }
        setEmail(session.user.email ?? null);
        const { data } = await supabase
          .from('users')
          .select('name')
          .eq('user_id', session.user.id)
          .single();
        setName(data?.name?.trim() ?? null);
      } catch {
        // silently ignore
      } finally {
        setLoadingUser(false);
      }
    })();
  }, []);

  const initials = name
    ? name.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()
    : email
    ? email[0].toUpperCase()
    : '?';

  const displayName = name ?? email ?? 'User';
  const planLabel = getLocalizedPlanLabel(planStatus.planKey, t);
  const planTimeline = formatPlanTimelineLocalized(
    t,
    planStatus.periodEnd,
    planStatus.daysLeft,
    planStatus.isExpired,
    i18n.language,
  );

  const handleSignOut = async () => {
    setSigningOut(true);
    try { await onSignOut(); } catch { setSigningOut(false); }
  };

  const menuItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '13px 16px',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    WebkitTapHighlightColor: 'transparent',
    transition: 'background 0.12s',
  };

  const cardStyle: React.CSSProperties = {
    background: 'var(--vscode-bg-elevated)',
    borderRadius: 16,
    border: '1px solid var(--vscode-border-primary)',
    overflow: 'hidden',
    boxShadow: 'var(--vscode-shadow-md)',
  };

  return (
    <div
      style={{
        minHeight: '100%',
        background: 'var(--vscode-bg-primary)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── Native "Me" header — compact, theme (no purple) ── */}
      <div
        style={{
          background: 'var(--ggj-header-bg)',
          minHeight: 56,
          paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 12px)',
          paddingBottom: 12,
          paddingLeft: 20,
          paddingRight: 20,
          borderRadius: 0,
          boxShadow: '0 1px 0 rgba(0,0,0,0.08)',
        }}
      >
        <h1 style={{ fontSize: 20, fontWeight: 800, color: 'var(--ggj-header-text, #fff)', margin: 0, letterSpacing: '-0.02em' }}>
          My Account
        </h1>
      </div>

      {/* ── Layered content card (rounded top, -12px overlap, theme bg; scrollbars hidden) ── */}
      <div
        style={{
          marginTop: -12,
          borderRadius: '24px 24px 0 0',
          background: 'var(--ggj-mobile-content-card-bg, var(--vscode-bg-primary))',
          borderTop: 'var(--ggj-mobile-content-card-border-top, none)',
          boxShadow: 'var(--ggj-mobile-content-card-shadow, none)',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <div
          className="mobile-scroll"
          data-ggj-native-scroll=""
          style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '0 16px 32px' }}
        >
      {/* ── User avatar card ── */}
      <div style={{ ...cardStyle, padding: 20, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
        {loadingUser ? (
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--vscode-border-primary, #e5e5e5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LoadingSpinner size="sm" showText={false} />
          </div>
        ) : (
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: 'var(--vscode-bg-tertiary)',
              border: '1px solid var(--vscode-border-primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--vscode-text-primary)',
              fontSize: 20,
              fontWeight: 800,
              flexShrink: 0,
              boxShadow: 'var(--vscode-shadow-sm)',
            }}
          >
            {initials}
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              fontSize: 17,
              fontWeight: 700,
              color: 'var(--vscode-text-primary, #000)',
              marginBottom: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {loadingUser ? '—' : displayName}
          </p>
          {email && (
            <p
              style={{
                fontSize: 12,
                color: 'var(--vscode-text-secondary, #616161)',
                marginBottom: 6,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {email}
            </p>
          )}
        </div>

        {!loadingUser && (
          <button
            type="button"
            onClick={() => onNavigate('profile-info')}
            style={{
              flexShrink: 0,
              width: 36,
              height: 36,
              borderRadius: 10,
              border: '1px solid var(--vscode-border-primary, #e5e5e5)',
              background: 'var(--vscode-bg-primary, #f3f3f3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
            aria-label="Edit profile"
          >
            <Settings size={16} style={{ color: 'var(--vscode-text-secondary, #616161)' }} />
          </button>
        )}
      </div>

      {/* Plan summary */}
      <div style={{ ...cardStyle, marginBottom: 12, padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <PlanIcon planId={planStatus.planId} planKey={planStatus.planKey} size={16} />
            <div style={{ minWidth: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--vscode-text-primary)', display: 'block' }}>
                {planLabel}
              </span>
              {planTimeline && (
                <span style={{ fontSize: 11, color: 'var(--vscode-text-secondary)', display: 'block' }}>
                  {planTimeline}
                </span>
              )}
            </div>
          </div>
          {(planStatus.planKey === 'free' || planStatus.isOutOfCredits || planStatus.planKey !== 'halfyearly') && (
            <button
              type="button"
              onClick={() => onNavigate('credits')}
              style={{
                border: 'none',
                background: 'none',
                color: 'var(--vscode-primary)',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                padding: 0,
                flexShrink: 0,
              }}
            >
              {planStatus.planKey === 'free'
                ? t('layout.profile.upgrade', 'Upgrade')
                : t('layout.profile.changePlan', 'Change plan')}
            </button>
          )}
        </div>
      </div>

      {/* ── Appearance toggle ── */}
      <div style={{ ...cardStyle, marginBottom: 12, padding: '14px 16px' }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--vscode-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 10px' }}>
          Appearance
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          {(
            [
              { pref: 'light' as ThemePreference, Icon: Sun,        label: 'Light' },
              { pref: 'auto'  as ThemePreference, Icon: Smartphone, label: 'Auto'  },
              { pref: 'dark'  as ThemePreference, Icon: Moon,       label: 'Dark'  },
            ] as { pref: ThemePreference; Icon: React.FC<{ size?: number }>; label: string }[]
          ).map(({ pref, Icon, label }) => {
            const active = themePref === pref;
            return (
              <button
                key={pref}
                type="button"
                onClick={() => handleThemePref(pref)}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 6,
                  padding: '10px 4px',
                  borderRadius: 12,
                  border: active
                    ? '1.5px solid var(--vscode-primary)'
                    : '1px solid var(--vscode-border-primary)',
                  background: active
                    ? 'var(--brand-accent-bg)'
                    : 'var(--vscode-bg-primary)',
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                  transition: 'all 0.15s',
                }}
              >
                <Icon size={18} />
                <span style={{
                  fontSize: 11,
                  fontWeight: active ? 700 : 500,
                  color: active ? 'var(--vscode-primary)' : 'var(--vscode-text-secondary)',
                }}>
                  {label}
                </span>
              </button>
            );
          })}
        </div>
        <p style={{ fontSize: 11, color: 'var(--vscode-text-tertiary)', margin: '10px 0 0', textAlign: 'center' }}>
          {themePref === 'auto'
            ? 'Follows time of day — light 6am–6pm, dark at night'
            : `Always ${themePref} theme`}
        </p>
      </div>

      {/* ── Menu groups ── */}
      {MENU_GROUPS.map((group, gi) => (
        <div key={gi} style={{ ...cardStyle, marginBottom: 12 }}>
          {group.items.map((item, ii) => (
            <React.Fragment key={item.id}>
              {ii > 0 && (
                <div style={{ height: 1, background: 'var(--vscode-border-primary, #e5e5e5)', margin: '0 16px' }} />
              )}
              <button
                type="button"
                style={menuItemStyle}
                onClick={() => item.page && onNavigate(item.page)}
              >
                <span style={{ color: 'var(--vscode-primary, #4f46e5)', flexShrink: 0 }}>
                  {item.icon}
                </span>
                <span
                  style={{
                    flex: 1,
                    fontSize: 14,
                    fontWeight: 500,
                    color: item.danger ? '#ef4444' : 'var(--vscode-text-primary, #000)',
                  }}
                >
                  {item.label}
                </span>
                <ChevronRight
                  size={16}
                  style={{ color: 'var(--vscode-text-secondary, #616161)', flexShrink: 0 }}
                />
              </button>
            </React.Fragment>
          ))}
        </div>
      ))}

      {/* ── Danger zone: Delete Account ── */}
      <div style={{ ...cardStyle, marginBottom: 12 }}>
        <button
          type="button"
          style={{ ...menuItemStyle }}
          onClick={() => setShowDeletionFlow(true)}
        >
          <Trash2 size={16} style={{ color: 'var(--vscode-error, #ef4444)', flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: 'var(--vscode-error, #ef4444)' }}>
            Delete Account
          </span>
          <ChevronRight size={16} style={{ color: 'var(--vscode-text-secondary)', flexShrink: 0 }} />
        </button>
      </div>

      {/* ── Sign out ── */}
      <div style={{ ...cardStyle, marginTop: 4 }}>
        <button
          type="button"
          style={{
            ...menuItemStyle,
            justifyContent: 'center',
            padding: '14px 16px',
          }}
          onClick={handleSignOut}
          disabled={signingOut}
        >
          {signingOut ? (
            <LoadingSpinner size="xs" showText={false} />
          ) : (
            <LogOut size={16} style={{ color: '#ef4444' }} />
          )}
          <span style={{ fontSize: 14, fontWeight: 600, color: '#ef4444' }}>
            {signingOut ? 'Signing out…' : 'Sign Out'}
          </span>
        </button>
      </div>

      {/* Account Deletion Flow */}
      <AccountDeletionFlow
        isOpen={showDeletionFlow}
        onClose={() => setShowDeletionFlow(false)}
        onComplete={() => { setShowDeletionFlow(false); onSignOut(); }}
      />

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        marginTop: 16,
        padding: '10px 4px 0',
        borderTop: '1px solid var(--vscode-border-primary)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <GGJLogoMark size={18} />
          <span style={{ fontSize: 11, color: 'var(--vscode-text-secondary)' }}>GoGrabJob</span>
        </div>
        <span style={{ fontSize: 11, color: 'var(--vscode-text-tertiary)' }}>v{APP_VERSION}</span>
      </div>
        </div>
      </div>
    </div>
  );
};

export default MobileProfileMenu;
